import api from "./api.service";

/**
 * Upload service for handling file uploads to Azure Blob Storage
 * Supports bulk upload of resumes with validation and progress tracking
 */
export const uploadService = {
  /**
   * Validate files before upload
   * @param {File[]} files - Array of files to validate
   * @returns {Object} Validation result with isValid flag and errors array
   */
  validateFiles: (files) => {
    const errors = [];
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const maxFiles = 50;

    // Check number of files
    if (!files || files.length === 0) {
      errors.push('No files selected');
      return { isValid: false, errors };
    }

    if (files.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed per upload`);
      return { isValid: false, errors };
    }

    // Validate each file
    files.forEach((file, index) => {
      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File size exceeds 10MB limit`);
      }

      // Check file type
      const fileName = file.name.toLowerCase();
      const isValidType = allowedTypes.some(type => fileName.endsWith(type));
      if (!isValidType) {
        errors.push(`${file.name}: Invalid file type. Only PDF, DOC, DOCX allowed`);
      }

      // Check for empty files
      if (file.size === 0) {
        errors.push(`${file.name}: File is empty`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Bulk upload resumes to Azure Blob Storage
   * @param {File[]} files - Array of files to upload
   * @param {Object} metadata - Additional metadata for the upload
   * @returns {Promise<Object>} Upload result with success status and file details
   */
  async bulkUploadResumes(files, metadata = null) {
    try {
      // Validate files first
      const validation = this.validateFiles(files);
      if (!validation.isValid) {
        throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
      }

      // Prepare form data
      const formData = new FormData();
      
      // Append files
      files.forEach(file => {
        formData.append('files', file);
      });

      // Append metadata
      if (metadata) {
        Object.keys(metadata).forEach(key => {
          formData.append(key, metadata[key]);
        });
      }

      // Make API call
      const response = await api.post('/upload/bulk-resumes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;

    } catch (error) {
      console.error('Error in bulkUploadResumes:', error);
      throw new Error(`Bulk upload failed: ${error.message}`);
    }
  },

  /**
   * Upload single file with progress tracking
   * @param {File} file - File to upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} Upload result
   */
  async uploadSingleFile(file, onProgress = null) {
    try {
      // Validate single file
      const validation = this.validateFiles([file]);
      if (!validation.isValid) {
        throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
      }

      const formData = new FormData();
      formData.append('files', file);

      const response = await api.post('/upload/bulk-resumes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              type: 'upload',
              progress: percentCompleted,
              message: `Uploading ${file.name}... ${percentCompleted}%`
            });
          }
        }
      });

      return response.data;

    } catch (error) {
      console.error('Error in uploadSingleFile:', error);
      throw new Error(`File upload failed: ${error.message}`);
    }
  },

  /**
   * Get upload status by batch ID
   * @param {string} batchId - Batch ID to check status for
   * @returns {Promise<Object>} Upload status
   */
  async getUploadStatus(batchId) {
    try {
      const response = await api.get(`/upload/status/${batchId}`);
      return response.data;
    } catch (error) {
      console.error('Error in getUploadStatus:', error);
      throw new Error(`Failed to get upload status: ${error.message}`);
    }
  },

  /**
   * Health check for upload service
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      const response = await api.get('/upload/health');
      return response.data;
    } catch (error) {
      console.error('Error in healthCheck:', error);
      throw new Error(`Health check failed: ${error.message}`);
    }
  }
}; 